import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { TCPA_LANGUAGE } from "./landing/config";
import {
  isValidEmail,
  isValidZip,
  INCIDENT_AGE_BUCKETS,
} from "./landing/publicFormValidators";
import { formatUSPhone, isValidUSPhone } from "./landing/publicFormPhone";
import {
  AFPI_CHOICES,
  requiresAtFaultPartyInsured,
} from "./landing/mvaApplicability";

/**
 * Iter-63.1.12A -- Hosted intake form.
 *
 * Full canonical MVA lead intake:
 *   - Universal minimum: first, last, phone, state, consent.
 *   - MVA qualification: accident_type, incident_age_bucket (bucket
 *     is REQUIRED; optional exact incident_date), at_fault,
 *     currently_represented, physically_injured, medical_treatment,
 *     and (when MVA) at_fault_party_insured (tri-state).
 *   - Optional: email, zip, address, city.
 *
 * Buyer-specific optional fields (police_report_filed, claimant_insured,
 * hit_and_run, uninsured_motorist, treatment_date, ongoing_treatment,
 * phone_verified) are supported by the /api/public/lead endpoint via
 * `PublicFormLeadIn` but NOT surfaced as universal consumer questions.
 *
 * TCPA disclosure MUST match the unified Conecme default (`TCPA_LANGUAGE`
 * in `./landing/config.js`, mirrored server-side by
 * `CONECME_DEFAULT_TCPA` in `backend/tcpa_disclosures.py`). A
 * `data-testid` on the disclosure text (`pf-tcpa-text`) lets Jest +
 * Playwright suites lock the frontend/backend drift guard. Privacy
 * Policy and Terms of Service links are provided for parity with the
 * standard landing footer.
 */

const ACCIDENT_TYPE_OPTIONS = [
  { value: "auto",                  label: "Auto accident" },
  { value: "truck",                 label: "Truck accident" },
  { value: "motorcycle",            label: "Motorcycle accident" },
  { value: "rideshare",             label: "Rideshare accident" },
  { value: "commercial_vehicle",    label: "Commercial vehicle accident" },
  { value: "public_transportation", label: "Public transportation accident" },
  { value: "pedestrian",            label: "Pedestrian accident" },
  { value: "bicycle",               label: "Bicycle accident" },
  { value: "other",                 label: "Other" },
];

export default function HostedForm() {
  const { publicId } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    // Universal minimum
    first_name: "", last_name: "", phone: "",
    state: "", consent: false,
    // MVA canonical qualification
    accident_type: "",
    incident_age_bucket: "",
    incident_date: "",
    at_fault: null,
    currently_represented: null,
    physically_injured: null,
    medical_treatment: null,
    at_fault_party_insured: null,
    // Optional contact / location
    email: "", address: "", city: "", zip: "",
    // Legacy alias for pre-Iter-63.1.12A callers.
    accident_date: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/public/campaign/${publicId}`)
      .then((r) => setCampaign(r.data))
      .catch(() => setErr("Campaign not found or inactive"));
  }, [publicId]);

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const afpiApplicable = useMemo(
    () => requiresAtFaultPartyInsured({ caseType: form.accident_type }),
    [form.accident_type],
  );

  const isValid = useMemo(() => {
    return Boolean(
      form.first_name.trim()
      && form.last_name.trim()
      && isValidUSPhone(form.phone)
      && form.state.trim()
      && form.consent === true
      && form.accident_type
      && form.incident_age_bucket
      && form.at_fault !== null
      && form.currently_represented !== null
      && form.physically_injured !== null
      && form.medical_treatment !== null
      && (form.email.trim() === "" || isValidEmail(form.email))
      && (form.zip.trim()   === "" || isValidZip(form.zip))
      && (!afpiApplicable || (typeof form.at_fault_party_insured === "string"
                              && form.at_fault_party_insured.length > 0)),
    );
  }, [form, afpiApplicable]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("You must consent to be contacted");
    if (!isValid) return toast.error("Please complete every required field");
    setLoading(true);
    try {
      await api.post("/public/lead", {
        campaign_public_id: publicId,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        state: form.state.trim(),
        zip: form.zip.trim() || null,
        address: form.address.trim() || null,
        city: form.city.trim() || null,
        accident_type: form.accident_type,
        // Canonical bucket + optional exact date. Never fabricated.
        incident_age_bucket: form.incident_age_bucket,
        incident_date: form.incident_date || null,
        // Legacy alias — kept in sync with incident_date if the operator
        // fills the optional exact-date field.
        accident_date: form.incident_date || form.accident_date || null,
        at_fault: form.at_fault,
        currently_represented: form.currently_represented,
        physically_injured: form.physically_injured,
        medical_treatment: form.medical_treatment,
        at_fault_party_insured: form.at_fault_party_insured || null,
        consent: form.consent,
      });
      setSubmitted(true);
      toast.success("Submitted");
    } catch (er) {
      toast.error(er.response?.data?.detail || "Submission failed");
    } finally { setLoading(false); }
  };

  if (err) return <Full center><div className="brut-card p-6 brut-shadow"><b>{err}</b></div></Full>;
  if (!campaign) return <Full center><div className="label-mono">Loading…</div></Full>;
  if (submitted) return (
    <Full center>
      <div className="brut-card p-8 brut-shadow-lg max-w-md">
        <div className="font-chivo font-black text-2xl uppercase tracking-tighter">Thank you</div>
        <div className="mt-2 text-sm">We received your information and will be in touch shortly.</div>
      </div>
    </Full>
  );

  return (
    <Full>
      <div className="mx-auto max-w-xl p-6">
        <div className="brut-card p-8 brut-shadow-lg">
          <div className="label-mono">Hosted intake</div>
          <div className="font-chivo font-black text-3xl uppercase tracking-tighter leading-none">{campaign.name}</div>
          <div className="mt-3 text-sm">Complete the form below. Fields marked * are required.</div>

          <form onSubmit={submit} className="mt-6 grid grid-cols-2 gap-3">
            <Field label="First name*" required v={form.first_name} onChange={(e) => patch("first_name", e.target.value)} testId="pf-first" />
            <Field label="Last name*" required v={form.last_name} onChange={(e) => patch("last_name", e.target.value)} testId="pf-last" />
            <Field label="Phone*" required v={form.phone}
              onChange={(e) => patch("phone", formatUSPhone(e.target.value))}
              placeholder="585-545-8888"
              testId="pf-phone" />
            <Field label="Email" type="email" v={form.email} onChange={(e) => patch("email", e.target.value)} testId="pf-email" />
            <Field label="Address" v={form.address} onChange={(e) => patch("address", e.target.value)} span={2} testId="pf-address" />
            <Field label="City" v={form.city} onChange={(e) => patch("city", e.target.value)} testId="pf-city" />
            <Field label="State*" required v={form.state} onChange={(e) => patch("state", e.target.value.toUpperCase().slice(0, 2))} testId="pf-state" placeholder="AL" />
            <Field label="ZIP" v={form.zip} onChange={(e) => patch("zip", e.target.value)} testId="pf-zip" />

            {/* Iter-63.1.12A — canonical MVA qualification block */}
            <Select label="Accident type*" span={2}
              v={form.accident_type}
              onChange={(e) => patch("accident_type", e.target.value)}
              testId="pf-accident-type"
              options={[{ value: "", label: "Select accident type…" }, ...ACCIDENT_TYPE_OPTIONS]}
            />
            <Select label="When did the accident happen?*" span={2}
              v={form.incident_age_bucket}
              onChange={(e) => patch("incident_age_bucket", e.target.value)}
              testId="pf-incident-age-bucket"
              options={[
                { value: "", label: "Select when the accident happened…" },
                ...INCIDENT_AGE_BUCKETS,
              ]}
            />
            <Field label="Exact accident date" type="date"
              v={form.incident_date}
              onChange={(e) => patch("incident_date", e.target.value)}
              testId="pf-incident-date"
              span={2}
            />

            <YesNoRow label="Were you at fault for the accident?*"
              value={form.at_fault} onChange={(v) => patch("at_fault", v)}
              testIdPrefix="pf-at-fault" />
            <YesNoRow label="Are you currently represented by an attorney?*"
              value={form.currently_represented} onChange={(v) => patch("currently_represented", v)}
              testIdPrefix="pf-represented" />
            <YesNoRow label="Were you physically injured?*"
              value={form.physically_injured} onChange={(v) => patch("physically_injured", v)}
              testIdPrefix="pf-injured" />
            <YesNoRow label="Have you received medical treatment?*"
              value={form.medical_treatment} onChange={(v) => patch("medical_treatment", v)}
              testIdPrefix="pf-treatment" />

            {afpiApplicable && (
              <TriStateRow label="Was the other driver or at-fault party insured?*"
                value={form.at_fault_party_insured}
                onChange={(v) => patch("at_fault_party_insured", v)}
                testIdPrefix="pf-afpi" />
            )}

            <label className="col-span-2 flex items-start gap-2 text-xs mt-2">
              <input data-testid="pf-consent" type="checkbox" checked={form.consent} onChange={(e) => patch("consent", e.target.checked)} />
              <span data-testid="pf-tcpa-text">{TCPA_LANGUAGE}</span>
            </label>
            <div className="col-span-2 text-[11px] text-zinc-600" data-testid="pf-legal-links">
              <Link to="/privacy" data-testid="pf-privacy-link" className="underline mr-3">Privacy Policy</Link>
              <Link to="/terms" data-testid="pf-terms-link" className="underline">Terms of Service</Link>
            </div>

            <button type="submit" disabled={loading || !isValid} data-testid="pf-submit"
              className="col-span-2 bg-black text-white border border-black py-3 uppercase text-xs tracking-widest font-bold hover:translate-x-[2px] hover:translate-y-[2px] hover:brut-shadow-sm transition-transform duration-100 disabled:opacity-60">
              {loading ? "Submitting…" : "Submit"}
            </button>
          </form>
        </div>
      </div>
    </Full>
  );
}

function Field({ label, v, onChange, type = "text", required = false, testId, span = 1, placeholder }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <label className="label-mono">{label}</label>
      <input type={type} required={required} value={v || ""} onChange={onChange}
        data-testid={testId}
        placeholder={placeholder}
        className="w-full border border-black px-3 py-2 mt-1" />
    </div>
  );
}

function Select({ label, v, onChange, testId, options, span = 1 }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <label className="label-mono">{label}</label>
      <select value={v || ""} onChange={onChange} data-testid={testId}
        className="w-full border border-black px-3 py-2 mt-1 bg-white">
        {options.map((o) => (
          <option key={o.value} value={o.value}
            data-testid={testId + "-opt-" + o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function YesNoRow({ label, value, onChange, testIdPrefix }) {
  return (
    <div className="col-span-2 grid grid-cols-[1fr,auto,auto] items-center gap-3">
      <span className="label-mono">{label}</span>
      <button type="button"
        className={"border border-black px-4 py-2 uppercase text-xs tracking-widest font-bold "
                   + (value === true ? "bg-black text-white" : "bg-white text-black")}
        onClick={() => onChange(true)}
        data-testid={`${testIdPrefix}-yes`}>Yes</button>
      <button type="button"
        className={"border border-black px-4 py-2 uppercase text-xs tracking-widest font-bold "
                   + (value === false ? "bg-black text-white" : "bg-white text-black")}
        onClick={() => onChange(false)}
        data-testid={`${testIdPrefix}-no`}>No</button>
    </div>
  );
}

function TriStateRow({ label, value, onChange, testIdPrefix }) {
  return (
    <div className="col-span-2 grid grid-cols-[1fr,auto,auto,auto] items-center gap-3">
      <span className="label-mono">{label}</span>
      {AFPI_CHOICES.map((c) => (
        <button key={c.value} type="button"
          className={"border border-black px-4 py-2 uppercase text-xs tracking-widest font-bold "
                     + (value === c.value ? "bg-black text-white" : "bg-white text-black")}
          onClick={() => onChange(c.value)}
          data-testid={`${testIdPrefix}-${c.value}`}>{c.label}</button>
      ))}
    </div>
  );
}

function Full({ children, center }) {
  return (
    <div className={`min-h-screen bg-[#f4f4f5] ${center ? "flex items-center justify-center" : ""}`}>
      {children}
    </div>
  );
}
